import { useEffect, useRef, useState } from 'react';
import { FaDownload, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../axiosInstance';
import { API_URL_All } from '../api';
import * as general from '../libs/general';
import AdLayout from '../components/AdLayout';
import DataTable from '../components/ui/DataTable';
import Paging from '../components/Paging';
import { CheckingTicket } from '../components/tickets/Tickets';
import TthReconciliationReview from '../components/orders/TthReconciliationReview';
import ReconciliationPaymentConfirmModal from '../components/reconciliation/ReconciliationPaymentConfirmModal';

/**
 * Tab "Danh sách TTH đã đối soát" — batch ở trạng thái Vào phiếu (14) và Đã đối soát (15).
 * Layout/lib-filter (icon filter trong <th>) mirror NormalInvoices.jsx (luồng Normal).
 */
function TthInvoices({ embedded = false }) {
  const [pageData, setPageData] = useState({ content: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [, setParams] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const hasRun = useRef(false);

  const [reviewData, setReviewData] = useState(null);
  const [paymentConfirm, setPaymentConfirm] = useState(null);

  const search = async (filter) => {
    if (window.callingTthInvoices) return;
    window.callingTthInvoices = true;
    setIsLoading(true);
    setErrorMessage('');

    var nextParams = general.getSession('search_tth_invoices');
    var formData = general.getFormData('#tth_invoice_filter');
    Object.assign(nextParams, formData);

    if (!hasRun.current) {
      hasRun.current = true;
      var oldParams = general.getSession('search_tth_invoices');
      Object.assign(nextParams, oldParams);
      setSearchQuery(nextParams.keyword || '');
    }
    if (filter) Object.assign(nextParams, filter);

    general.setSession('search_tth_invoices', nextParams);
    setParams(nextParams);
    if (window.initializeFilterValue) window.initializeFilterValue(nextParams);

    var filteredParams = Object.fromEntries(
      Object.entries(nextParams).filter(([key, v]) => v !== undefined && v !== '' && key + v !== 'page0' && key + v !== 'size550')
    );
    // Tab này chỉ hiển thị Vào phiếu/Đã đối soát — mặc định cả 2 nếu người dùng chưa tự chọn qua
    // filter-ib cột "Trạng thái". CHỈ áp dụng cho request thực tế (filteredParams), KHÔNG ghi vào
    // nextParams/session/initializeFilterValue — nếu không, filter-ib "Trạng thái" sẽ luôn hiện sẵn 1
    // filter đang áp dụng (chip "Vào phiếu; Đã đối soát" không đóng được vì search() tiếp theo lại tự
    // set lại y hệt).
    if (!filteredParams.rateStatus) {
      filteredParams.rateStatus = 'Vào phiếu;Đã đối soát';
    }

    try {
      const res = await axiosInstance.get('/api/tth-invoices/searchTthInvoices', { params: filteredParams });
      setPageData(res.data);
    } catch (err) {
      setErrorMessage(err.message);
      setPageData({ content: [] });
    } finally {
      window.callingTthInvoices = false;
      setIsLoading(false);
    }
  };

  useEffect(() => { search(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    search({ page: 0, keyword: e.target.value });
  };
  const handlePageChange = (p) => search({ page: p - 1 });
  const handleItemsPerPageChange = (e) => search({ page: 0, size: parseInt(e.target.value, 10) });

  const content = pageData.page?.content || [];

  const handlePaymentSuccess = () => {
    setPaymentConfirm(null);
    search();
  };

  const [exportingId, setExportingId] = useState(null);

  const handleExport = async (item) => {
    setExportingId(item.id);
    try {
      const requestApi = `${API_URL_All}/api/tth-invoices/exportTthInvoice?id=${item.id}`;
      await general.downloadFile(requestApi, `Doi_soat_tth_${item.id}.xlsx`);
    } finally {
      setExportingId(null);
    }
  };

  const handleDelete = (item) => {
    general.openConfirm(async () => {
      try {
        const res = await axiosInstance.get('/api/tth-invoices/deleteTthInvoice', { params: { id: item.id } });
        if (!res.data?.success) {
          toast.error(res.data?.message || 'Xóa thất bại');
          return;
        }
        toast.success(res.data.message || 'Đã xóa');
        search();
      } catch (err) {
        toast.error(err.response?.data?.message || err.message);
      }
    }, `Xóa kỳ đối soát ${item.code}? Công nợ liên quan (nếu có) cũng sẽ bị hủy.`, 'Xác nhận xóa');
  };

  const inner = (
    <div className="table-responsive web-table">
      <div className="container-fluid pt-2">
        <div className="text-start mb-2 normal-form">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-6">
              <form id="tth_invoice_filter">
                <div className="row g-2 align-items-center">
                  <div className="col-8 col-sm-9">
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      name="keyword"
                      placeholder="Tìm theo mã kỳ đối soát / mã đối tác..."
                      autoComplete="off"
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <div className="col-4 col-sm-3">
                    <button onClick={() => search({ page: 0 })} type="button" className="btn btn-info btn-sm">Tìm kiếm</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <section className="p-1">
        <DataTable maxHeight="calc(100vh - 220px)" className="table-normal" data-loading={isLoading}>
          <table className="table table-striped table-bordered border-normal">
            <thead className="table-primary">
              <tr>
                <th className="sticky text-center" width="30px">STT</th>
                <th className="text-center"><span>Ngày tạo</span></th>
                <th className="text-center">
                  <span>Mã Đối tác</span>
                  <span className="filter-ib" data-form="tth_invoice_filter" data-filter="partners"
                    data-source-url={API_URL_All + '/api/contacts/searchContactCodes'} data-min="100px"><i className="filter-i"></i></span>
                </th>
                <th className="text-start"><span>Tên đối tác</span></th>
                <th className="text-center"><span>Số đơn</span></th>
                <th className="text-center"><span>Tổng Tệ</span></th>
                <th className="text-center"><span>Tổng chênh lệch</span></th>
                <th className="text-start">
                  <span>Trạng thái</span>
                  <span className="filter-ib" data-form="tth_invoice_filter" data-filter="rateStatus"
                    data-source-key="list_tth_invoice_rateStatus" data-min="100px"><i className="filter-i"></i></span>
                </th>
                <th><span>Thao tác</span></th>
              </tr>
            </thead>
            <tbody>
              {content.map((item, index) => (
                <tr key={item.id}>
                  <td className="sticky text-center">{(pageData.page.number * pageData.page.size) + index + 1}</td>
                  <td className="text-center" title={general.formatTimeShort(item.createdAt)}>{general.formatDate(item.createdAt)}</td>
                  <td className="text-center">{item.partnerCode}</td>
                  <td className="text-start">{item.partnerName}</td>
                  <td className="text-center">{item.orderCount}</td>
                  <td className="text-center">{general.formatDecimal(item.totalQuantity)}</td>
                  <td className="text-center">{general.formatNumber(item.totalAmountDiff)}</td>
                  <td className="text-start">
                    <CheckingTicket checkingId={item.checkingId} checkingName={item.checkingName} />
                    <span className="ms-1">{item.checkingName}</span>
                    {item.checkingId === 14 && (
                      <span className="btn-sm btn btn-primary ms-2" onClick={() => setPaymentConfirm(item)}>Xác nhận đối soát</span>
                    )}
                  </td>
                  <td>
                    <span className="small-btn" onClick={() => setReviewData({ id: item.id })}>xem</span>
                    <span
                      className="small-btn ms-2"
                      onClick={exportingId === item.id ? undefined : () => handleExport(item)}
                      title="Xuất excel"
                    >
                      {exportingId === item.id ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> : <FaDownload />}
                    </span>
                    {item.checkingId !== 15 && (
                      <span className="small-btn ms-2 text-danger" onClick={() => handleDelete(item)}><FaTrash /></span>
                    )}
                  </td>
                </tr>
              ))}
              {!isLoading && errorMessage === '' && content.length === 0 && (
                <tr><td colSpan="9" className="text-center">Không tìm thấy dữ liệu</td></tr>
              )}
              {errorMessage !== '' && (
                <tr><td colSpan="9" className="text-center">{errorMessage}</td></tr>
              )}
            </tbody>
          </table>
        </DataTable>
      </section>

      <div className="my-1 d-flex justify-content-end align-items-center">
        <Paging pageData={pageData} onPageChange={handlePageChange} onItemsPerPageChange={handleItemsPerPageChange} />
      </div>

      <input type="hidden" id="list_tth_invoice_rateStatus" value="Vào phiếu,Đã đối soát" />

      {reviewData && (
        <TthReconciliationReview data={reviewData} handleSearch={search} canEdit={true} onClose={() => setReviewData(null)} />
      )}

      {paymentConfirm && (
        <ReconciliationPaymentConfirmModal
          show={!!paymentConfirm}
          onHide={() => setPaymentConfirm(null)}
          onSuccess={handlePaymentSuccess}
          completeApi="/api/tth-invoices/completeTthInvoice"
          payloadBase={{ id: paymentConfirm.id }}
          summary={{
            id: paymentConfirm.id,
            partyCode: paymentConfirm.partnerCode,
            partyName: paymentConfirm.partnerName,
            amount: paymentConfirm.totalAmountDiff,
            orderCount: paymentConfirm.orderCount,
            dateText: paymentConfirm.createdAt ? general.formatDate(paymentConfirm.createdAt) : '',
          }}
          accountLabel="Tài khoản nhận tiền chênh lệch"
        />
      )}
    </div>
  );

  if (embedded) return inner;
  return <AdLayout>{inner}</AdLayout>;
}

export default TthInvoices;
